import { CSSProperties, FC } from 'react';

export type SortDirection = 'asc' | 'desc';

interface SortIconProps {
  direction?: SortDirection;
  className?: string;
  style?: CSSProperties;
}

const STROKE = 'rgba(255, 255, 255, 0.40)';
const STROKE_WIDTH = 1.86667;
const STROKE_PROPS = {
  stroke: STROKE,
  strokeWidth: STROKE_WIDTH,
  strokeLinecap: 'round' as const,
};

// Vectors 21–24: horizontal lines, ordered widest → narrowest.
// Rendered in this fixed DOM order; visual position is controlled
// by translateY so direction changes animate as a reshuffle.
const LINES = [
  { w: 15, end: 13.9998 }, // Vector 21
  { w: 14, end: 12.1331 }, // Vector 22
  { w: 12, end: 10.2664 }, // Vector 23
  { w: 10, end: 8.39977 }, // Vector 24
];

const LINE_HEIGHT = 2;
const LINE_GAP = 4;
const SLOT = LINE_HEIGHT + LINE_GAP;

const TRANSITION = 'transform 350ms cubic-bezier(0.4, 0.0, 0.2, 1)';

const SortIcon: FC<SortIconProps> = ({ direction = 'desc', className, style }) => {
  const isDesc = direction === 'desc';

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        mixBlendMode: 'plus-lighter',
        ...style,
      }}
    >
      {/* Vector 27 shaft + Vector 26 chevron — rotates 180° to flip between desc/asc */}
      <svg
        width="10"
        height="23"
        viewBox="0 0 10 23"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transform: isDesc ? 'rotate(0deg)' : 'rotate(180deg)',
          transformOrigin: '50% 50%',
          transition: TRANSITION,
        }}
      >
        <path d="M5 0.93335V21.4667" {...STROKE_PROPS} />
        <path
          d="M1.43311 17.90305L5.00145 21.47139C5.09257 21.56251 5.24031 21.56251 5.33143 21.47139L8.89977 17.90305"
          {...STROKE_PROPS}
        />
      </svg>

      {/* Vectors 21–24 lines — DOM order is fixed widest→narrowest;
          translateY moves each line to its target slot per direction */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${LINE_GAP}px`,
        }}
      >
        {LINES.map(({ w, end }, i) => {
          // desc: natural order (widest at top) → no offset
          // asc:  reversed (widest at bottom) → slot offset = (3 - 2i)
          const slotOffset = isDesc ? 0 : 3 - 2 * i;
          return (
            <svg
              key={i}
              width={w}
              height={LINE_HEIGHT}
              viewBox={`0 0 ${w} 2`}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: `translateY(${slotOffset * SLOT}px)`,
                transition: TRANSITION,
              }}
            >
              <path d={`M0.933105 0.93335H${end}`} {...STROKE_PROPS} />
            </svg>
          );
        })}
      </div>
    </div>
  );
};

export default SortIcon;
